import React from 'react';
import { MapPin, Calendar, FileText, Users, Heart, Shield, Zap } from 'lucide-react';

const WelcomePage = ({ onGetStarted }) => {
  const features = [
    {
      icon: MapPin,
      title: 'Accessibility Map',
      description: 'Find venues with detailed accessibility information, reviews from families, and real-time updates.'
    },
    {
      icon: Calendar,
      title: 'Inclusive Events',
      description: 'Discover sensory-friendly, adaptive, and inclusive events happening in your area with accommodation details.'
    },
    {
      icon: FileText,
      title: 'IEP Resource Hub',
      description: 'AI-powered tools to help create, improve, and understand IEP goals with state-specific guidance.'
    },
    {
      icon: Users,
      title: 'Parent Community',
      description: 'Connect with other families, share experiences, and find support through our discussion forums.'
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Family-Focused',
      description: 'Designed specifically for families with children with disabilities'
    },
    {
      icon: Shield,
      title: 'Accessibility First',
      description: 'Built with screen reader support, keyboard navigation, and inclusive design principles'
    },
    {
      icon: Zap,
      title: 'AI-Powered Tools',
      description: 'Intelligent IEP goal generation and disability resource discovery'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="welcome-title">
            Welcome to AbleFam
          </h1>
          <p className="text-xl md:text-2xl mb-4 text-blue-100">
            Your comprehensive hub for accessibility, inclusion, and family support
          </p>
          <p className="text-lg mb-8 text-blue-50 max-w-2xl mx-auto">
            Whether you're searching for wheelchair-accessible venues, planning sensory-friendly outings, or navigating IEP meetings — we're here to help. Our community of families understands the daily challenges and celebrates every victory with you.
          </p>
          <button
            onClick={onGetStarted}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-600"
            data-testid="get-started-btn"
          >
            Get Started
          </button>
          
          {/* Trust indicators */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-blue-200 text-sm">
            <span className="flex items-center"><span className="text-green-400 mr-1">✓</span> Free to use</span>
            <span className="flex items-center"><span className="text-green-400 mr-1">✓</span> Parent-verified venues</span>
            <span className="flex items-center"><span className="text-green-400 mr-1">✓</span> Real community support</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Everything You Need in One Place
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={32} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Why Choose AbleFam?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={24} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Join Our Growing Community
          </h2>
          <p className="text-xl text-white mb-6">
            Connect with thousands of families who are making their communities more accessible and inclusive.
          </p>
          
          {/* Reassurance Message */}
          <div className="inline-flex items-center bg-blue-800 px-4 py-2 rounded-full mb-8">
            <span className="text-yellow-300 mr-2">💛</span>
            <span className="text-white text-sm font-medium">
              You're in the right place. Built by parents who understand your journey.
            </span>
          </div>
          
          <div>
            <button
              onClick={onGetStarted}
              className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-600"
              data-testid="join-community-btn"
            >
              Join AbleFam Today
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WelcomePage;